import { type Scope, createContextScope } from '@radix-ui/react-context'
import React, { useMemo, useState } from 'react'
import {
  cardCategoryBoxVariants,
  cardCategoryContainerVariants,
} from './card-category.styles'
import type { CardCategoryProps } from './card-category.types'
import { Icon } from './components/icon'
import { Label } from './components/label'
import { cn } from '~/utils/cn'
import { useTheme } from '~/hooks/use-theme'

type CardCategoryContext = Omit<CardCategoryProps, 'children'> & {
  isHovered: boolean
  isPressed: boolean
}
const DISPLAY_NAME = 'CardCategory'

const Root = (props: CardCategoryProps) => {
  const {
    children,
    orientation = 'horizontal',
    testID,
    disabled,
    onClick,
    context,
    __scopeCardCategory,
  } = props as ScopedProps<CardCategoryProps>

  const theme = useTheme()

  const [isHovered, setIsHovered] = useState(false)
  const [isPressed, setIsPressed] = useState(false)

  const renderChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      if (child.type === Label && orientation === 'horizontal') {
        return child
      }
      if (child.type !== Label) {
        return child
      }
    }
    return null
  })

  const buttonBackgrounColor = useMemo(() => {
    if (!disabled && isPressed) return theme.colorBrowsingFillOnbrandPressed
    if (!disabled && isHovered) return theme.colorBrowsingFillOnbrandHovered
    if (disabled) return theme.colorBrowsingFillOnbrandDisabled
    return theme.colorBrowsingFillOnbrandEnabled
  }, [disabled, isHovered, isPressed, theme])

  const orientationProp = useMemo(() => {
    if (context) return 'horizontal'
    return orientation
  }, [context, orientation])
  return (
    <CardCategoryProvider
      scope={__scopeCardCategory}
      orientation={orientationProp}
      disabled={disabled}
      data-testid={testID}
      isHovered={isHovered}
      isPressed={isPressed}
      context={context}
    >
      <div
        data-testid={testID}
        className={cardCategoryBoxVariants({ disabled, orientation, context })}
      >
        <button
          type="button"
          className={cn(
            cardCategoryContainerVariants({
              orientation,
              disabled,
              context,
            })
          )}
          onClick={onClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onMouseDown={() => setIsPressed(true)}
          onMouseUp={() => setIsPressed(false)}
          style={{
            backgroundColor:
              context === 'brand' ? buttonBackgrounColor : undefined,
          }}
          disabled={disabled}
        >
          {renderChildren}
        </button>
        {orientation === 'vertical' && (
          <Label>
            {React.Children.toArray(children).find(
              child => React.isValidElement(child) && child.type === Label
            )}
          </Label>
        )}
      </div>
    </CardCategoryProvider>
  )
}

/*
----------------------------------------------------------------
Scope Definition
----------------------------------------------------------------
*/

export type ScopedProps<P> = P & {
  __scopeCardCategory?: Scope
}

const [createCardCategoryContext] = createContextScope(DISPLAY_NAME)

export const [CardCategoryProvider, useCardCategoryContext]: readonly [
  ProviderType<CardCategoryContext>,
  (consumerName: string, scope: Scope) => CardCategoryContext,
] = createCardCategoryContext<CardCategoryContext>(DISPLAY_NAME)

/*
----------------------------------------------------------------
Composition Export
----------------------------------------------------------------
*/

export const CardCategory = {
  Root,
  Icon,
  Label,
}
